import { Controller } from "@hotwired/stimulus"

// QAN-405: lightweight canvas signature pad for the Credit Repair Documents
// step. Captures a hand-drawn signature and stores it as a PNG data URL in a
// hidden field (credit_repair[signature_data]) that the controller decodes and
// attaches to the signature_document Active Storage attachment.
export default class extends Controller {
  static targets = ["canvas", "input", "saveButton"]
  static values = { existing: String }

  connect() {
    this.drawing = false
    this.hasSignature = false
    this.prefilledOnly = false
    this.setupCanvas()

    this.pointerDown = this.startStroke.bind(this)
    this.pointerMove = this.moveStroke.bind(this)
    this.pointerUp = this.endStroke.bind(this)

    this.canvasTarget.addEventListener("pointerdown", this.pointerDown)
    this.canvasTarget.addEventListener("pointermove", this.pointerMove)
    window.addEventListener("pointerup", this.pointerUp)
    this.resizeHandler = this.setupCanvas.bind(this)
    window.addEventListener("resize", this.resizeHandler)
  }

  disconnect() {
    this.canvasTarget.removeEventListener("pointerdown", this.pointerDown)
    this.canvasTarget.removeEventListener("pointermove", this.pointerMove)
    window.removeEventListener("pointerup", this.pointerUp)
    window.removeEventListener("resize", this.resizeHandler)
  }

  setupCanvas() {
    const canvas = this.canvasTarget
    const ratio = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    // Preserve a drawing if one exists before resizing.
    const existing = this.hasSignature ? canvas.toDataURL("image/png") : null

    // The canvas height comes from CSS, not the HTML height attribute, so the
    // drawing buffer stays in sync with the box the user sees and never inflates.
    this.cssWidth = rect.width
    this.cssHeight = rect.height
    canvas.width = rect.width * ratio
    canvas.height = rect.height * ratio

    const ctx = canvas.getContext("2d")
    ctx.scale(ratio, ratio)
    ctx.lineWidth = 2
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.strokeStyle = "#111111"
    this.ctx = ctx

    if (existing) {
      const img = new Image()
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height)
      img.src = existing
    } else if (this.hasExistingValue && this.existingValue) {
      // A signature was captured on a prior visit and attached to
      // signature_document. Paint it back so returning to this step shows the
      // saved signature instead of an empty pad. We only display it — we do NOT
      // write it into the hidden field, so re-submitting the step without
      // re-signing leaves signature_data blank and the server keeps the already
      // attached blob (the model treats an attached signature as satisfied)
      // instead of decoding and re-attaching a fresh blob every visit.
      this.hasSignature = true
      this.prefilledOnly = true
      const img = new Image()
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height)
      img.src = this.existingValue
    } else {
      this.renderGuides()
    }
  }

  // Draw the "Sign here" placeholder and baseline shown in the mockup. They are
  // wiped the moment the user starts drawing so they never end up in the saved
  // PNG, and are restored on clear.
  renderGuides() {
    if (!this.ctx || this.hasSignature) return
    const ctx = this.ctx
    const baseline = this.cssHeight - 24

    ctx.save()
    ctx.strokeStyle = "#d1d5db"
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(16, baseline)
    ctx.lineTo(this.cssWidth - 16, baseline)
    ctx.stroke()

    ctx.fillStyle = "#9ca3af"
    ctx.font = "italic 22px sans-serif"
    ctx.textBaseline = "alphabetic"
    ctx.fillText("Sign here", 16, baseline - 6)
    ctx.restore()
  }

  clearGuides() {
    this.ctx.clearRect(0, 0, this.cssWidth, this.cssHeight)
  }

  position(event) {
    const rect = this.canvasTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  startStroke(event) {
    event.preventDefault()
    // A signature that was only prefilled (painted from a prior visit but never
    // written to the hidden field) must be treated as a blank pad the moment the
    // user draws: wipe the prefilled image and forget the stale existingValue so
    // the new stroke is what gets saved and submitted.
    if (this.prefilledOnly) {
      this.prefilledOnly = false
      this.hasSignature = false
      this.existingValue = ""
      this.clearGuides()
    }
    if (!this.hasSignature) this.clearGuides()
    this.drawing = true
    const { x, y } = this.position(event)
    this.ctx.beginPath()
    this.ctx.moveTo(x, y)
  }

  moveStroke(event) {
    if (!this.drawing) return
    event.preventDefault()
    const { x, y } = this.position(event)
    this.ctx.lineTo(x, y)
    this.ctx.stroke()
    this.hasSignature = true
  }

  endStroke() {
    if (!this.drawing) return
    this.drawing = false
    this.save()
  }

  save() {
    if (this.hasInputTarget && this.hasSignature) {
      this.inputTarget.value = this.canvasTarget.toDataURL("image/png")
    }
    this.toggleSaveButton(this.hasSignature)
  }

  clear() {
    this.clearGuides()
    this.hasSignature = false
    // Drop the saved signature so a later setupCanvas() (e.g. a mobile resize
    // fired by the address bar collapsing) can't repaint the old drawing over a
    // fresh one and silently discard what the user just signed.
    this.existingValue = ""
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    this.renderGuides()
    this.toggleSaveButton(false)
  }

  // The dashboard Documents block gates its submit button on a drawn signature.
  // Reveal it once the user has signed, hide it again when the pad is cleared.
  toggleSaveButton(visible) {
    if (!this.hasSaveButtonTarget) return
    this.saveButtonTarget.classList.toggle("hidden", !visible)
  }
}
